import React, { createContext, useState, useEffect, useContext } from 'react';
import { api } from '@/api';

const CustomerAuthContext = createContext();

export function CustomerAuthProvider({ children }) {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if customer is logged in
    checkCustomerAuth();

    // Listen for logout events
    const handleLogout = () => {
      setCustomer(null);
    };

    const handleRefresh = () => {
      checkCustomerAuth();
    };

    window.addEventListener('customer:logout', handleLogout);
    window.addEventListener('customer:refresh', handleRefresh);
    return () => {
      window.removeEventListener('customer:logout', handleLogout);
      window.removeEventListener('customer:refresh', handleRefresh);
    };
  }, []);

  async function checkCustomerAuth() {
    try {
      const data = await api.customerMe();
      if (data?.ok && data?.customer) {
        setCustomer(data.customer);
      } else {
        setCustomer(null);
      }
    } catch (error) {
      setCustomer(null);
    } finally {
      setLoading(false);
    }
  }

  async function register(email, password, firstName, lastName, dateOfBirth) {
    const resp = await api.customerRegister({ email, password, firstName, lastName, dateOfBirth });
    if (resp?.requiresEmailVerification) {
      return { success: true, requiresEmailVerification: true };
    }
    if ((resp?.ok || resp?.customer) && resp?.customer) {
      setCustomer(resp.customer);
      return { success: true, customer: resp.customer };
    }
    throw new Error(resp?.error || 'Registration failed');
  }

  async function login(email, password, remember = false, twoFactorToken = undefined) {
    const resp = await api.customerLogin({ email, password, remember, twoFactorToken });
    if (resp?.requiresTwoFactor) {
      return { success: false, requiresTwoFactor: true, method: resp.method || 'TOTP' };
    }
    if ((resp?.ok || resp?.customer) && resp?.customer) {
      setCustomer(resp.customer);
      return { success: true, customer: resp.customer };
    }
    throw new Error(resp?.error || 'Login failed');
  }

  async function logout() {
    await api.customerLogout();
    setCustomer(null);
    window.dispatchEvent(new CustomEvent('customer:logout'));
  }

  async function updateProfile(data) {
    const resp = await api.customerUpdateProfile(data);
    if (resp?.ok && resp?.customer) {
      setCustomer(resp.customer);
      return { success: true, customer: resp.customer };
    }
    throw new Error(resp?.error || 'Profile update failed');
  }

  async function deleteAccount() {
    const resp = await api.customerDeleteAccount();
    if (resp?.ok) {
      setCustomer(null);
      window.dispatchEvent(new CustomEvent('customer:logout'));
      return { success: true };
    }
    throw new Error(resp?.error || 'Account deletion failed');
  }

  const value = {
    customer,
    loading,
    isAuthenticated: !!customer,
    register,
    login,
    logout,
    updateProfile,
    deleteAccount,
    checkCustomerAuth,
  };

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  );
}

export function useCustomerAuth() {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within CustomerAuthProvider');
  }
  return context;
}
